import _axios from '../api';
import * as storeActions from './storeActions';
import { queryParams } from '../utils';

const API = '/backups';

/**
 * The api layer rejects with the response, so the platform error body is on `data`. A network-level
 * failure has no response and arrives as the axios error instead, hence the guards.
 */
function actionErrorMessage(err, fallback) {
  const body = (err && err.data) || {};
  return body.message || (err && err.message) || fallback;
}

export function getAllByStore(storeId, page = 0, size = 20, sort = 'id', sortDir = 'DESC', searchRequest = null) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({ type: 'BACKUP_LOADING' });
      _axios
        .get(`${API}/store/${storeId}?page=${page}&size=${size}&sort=${sort},${sortDir}${queryParams(searchRequest)}`)
        .then(response => {
          dispatch({
            type: 'BACKUP_GETALL_SUCCESS',
            payload: response.data,
            query: { storeId, page, size, sort, sortDir, searchRequest }
          });
          resolve(response.data);
        })
        .catch(err => {
          dispatch({
            type: 'BACKUP_GETALL_ERROR',
            payload: err.message
          });
          reject(err);
        });
    });
}

export function getAllCloneableByStore(environment, storeId, page = 0, size = 20, sort = 'id', sortDir = 'DESC') {
  return async dispatch => {
    dispatch({ type: 'BACKUP_LOADING' });
    try {
      let response;
      // Kept with the rows rather than read from component state later: a request that resolves after the
      // user has switched environments still lands its own rows, and they must carry their own provenance.
      const foreign = environment.name.toLowerCase() !== process.env.NODE_ENV;
      if (!foreign) {
        response = await _axios.get(
          `${API}/store/${storeId}?page=${page}&size=${size}&sort=${sort},${sortDir}&fuzzyString=DONE`
        );
      } else {
        response = await _axios.get(
          `${API}/store/${storeId}?environment=${environment.name}&page=${page}&size=${size}&sort=${sort},${sortDir}&fuzzyString=DONE`
        );
      }
      dispatch({
        type: 'BACKUP_GETALL_CLONEABLE_SUCCESS',
        payload: response.data,
        foreign
      });
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'BACKUP_GETALL_CLONEABLE_ERROR',
        payload: err.message
      });
    }
  };
}

export function create(storeId, databaseOnly = false) {
  // Returns the promise so the caller can keep the dialog open until the backup is actually queued.
  return function(dispatch) {
    return _axios
      .post(`${API}/store/${storeId}`, null, { params: { databaseOnly } })
      .then(() => {
        dispatch({
          type: 'BACKUP_CREATE_SUCCESS'
        });
        dispatch(getAllByStore(storeId));
        return true;
      })
      .catch(err => {
        // The api layer rejects with the response, so err.message is undefined for every HTTP error and
        // the dialog's alert would stay hidden on the common path.
        dispatch({
          type: 'BACKUP_CREATE_ERROR',
          payload: actionErrorMessage(err, 'Create failed')
        });
        // Resolves false rather than rejecting: the error is already in the store, and the caller only
        // needs to know whether to close its dialog.
        return false;
      });
  };
}

export function restore(storeId, backup) {
  return function(dispatch) {
    _axios
      .post(`${API}/store/${storeId}/restore`, backup)
      .then(() => {
        dispatch({
          type: 'BACKUP_RESTORE_SUCCESS'
        });
        dispatch(getAllByStore(storeId));
      })
      .catch(err => {
        dispatch({
          type: 'BACKUP_RESTORE_ERROR',
          payload: err.message
        });
      });
  };
}

export function remove(storeId, backupId) {
  return function(dispatch) {
    _axios
      .delete(API + '/' + backupId)
      .then(() => {
        dispatch({
          type: 'BACKUP_DELETE_SUCCESS'
        });
        dispatch(getAllByStore(storeId));
      })
      .catch(err => {
        // The server refuses a backup that is still running, so the reason has to reach the user.
        dispatch({
          type: 'BACKUP_DELETE_ERROR',
          payload: actionErrorMessage(err, 'Delete failed')
        });
      });
  };
}

/**
 * Replays the query that produced the rows on screen. Falling back to getAllByStore's defaults would silently
 * drop the page, sort and search the user is looking at; the guard covers a list belonging to another store.
 */
function reloadCurrentList(getState, storeId) {
  const state = getState();
  const q = (state.backup && state.backup.lastQuery) || {};
  // Loose compare: a store id is a string from the route but a number from the loaded store.
  if (q.storeId == null || q.storeId != storeId) {
    return getAllByStore(storeId);
  }
  return getAllByStore(storeId, q.page, q.size, q.sort, q.sortDir, q.searchRequest);
}

/**
 * Both endpoints return the updated backup, so the row is replaced from the response rather than refetching
 * the page — no flicker, and the current page and search stay put.
 *
 * A response with no usable row falls back to a refetch instead of being dropped. A service still running the
 * earlier build answers 200 with an empty body, and merging that by id matches nothing: the pin persists but
 * the row never changes, which reads as the button doing nothing at all.
 */
function applyUpdatedBackup(dispatch, getState, type, storeId, response) {
  const updated = response && response.data;
  if (updated && updated.id) {
    dispatch({ type, payload: updated });
    return;
  }
  console.warn(`${type} returned no backup; refetching the list instead`, updated);
  dispatch({ type, payload: null });
  dispatch(reloadCurrentList(getState, storeId));
}

export function pin(storeId, backupId, note) {
  return function(dispatch, getState) {
    _axios
      .put(`${API}/${backupId}/pin`, { note })
      .then(response => applyUpdatedBackup(dispatch, getState, 'BACKUP_PIN_SUCCESS', storeId, response))
      .catch(err => {
        dispatch({
          type: 'BACKUP_PIN_ERROR',
          payload: actionErrorMessage(err, 'Pin failed')
        });
      });
  };
}

export function unpin(storeId, backupId) {
  return function(dispatch, getState) {
    _axios
      .put(`${API}/${backupId}/unpin`)
      .then(response => applyUpdatedBackup(dispatch, getState, 'BACKUP_UNPIN_SUCCESS', storeId, response))
      .catch(err => {
        dispatch({
          type: 'BACKUP_UNPIN_ERROR',
          payload: actionErrorMessage(err, 'Unpin failed')
        });
      });
  };
}

export function select(dataCentreSelected) {
  return function(dispatch) {
    // update selected region
    Promise.all([
      dispatch({
        type: 'BACKUP_SELECT',
        payload: dataCentreSelected
      }),
      // refresh ALL data based on the region selected
      dispatch(storeActions.getAllByDataCentre(dataCentreSelected))
    ]);
    localStorage.setItem('dataCentreSelected', JSON.stringify(dataCentreSelected));
  };
}

export function clear() {
  return function(dispatch) {
    dispatch({
      type: 'BACKUP_CLEAR'
    });
  };
}

export function clearError() {
  return function(dispatch) {
    dispatch({
      type: 'BACKUP_CLEAR_ERROR'
    });
  };
}
